import React from 'react';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { FieldValues, Path, PathValue, useFormContext, useWatch } from 'react-hook-form';
import EmailAutocomplete from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/components/ui/EmailAutocomplete';
import AlertStyledInput from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/components/ui/AlertStyledInput';
import PathControlledTextArea from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/components/ui/PathControlledTextArea';
import { NotificationMethodSchema, FrequencySchema } from '../schema/AlertBuilderSchema';
import { FrequencyType } from '../schema/AlertBuilderSchemaTypes';
import ControlledMultiSelect from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/components/ui/ControlledMultiSelect';
import { DROPDOWN_WIDTH } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/utils';
import PathControlledDropdown, {
  DropdownOption
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/components/ui/PathControlledDropdown';
import { Box, FormControlLabel, Radio, RadioGroup, IconButton, Popover, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import colors from 'src/utils/colors';

const DISABLED_NOTIFICATION_METHODS = ['Slack', 'Teams'];

// Bearer Token field with info icon component
function BearerTokenFieldWithInfo<T extends FieldValues>() {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <Box sx={{ position: 'relative' }}>
      <PathControlledTextArea<T>
        name={'notifications.config.webhook.bearerToken' as Path<T>}
        label={
          <Flex alignItems="center" gap={6}>
            Bearer Token
            <IconButton
              size="medium"
              onMouseEnter={handlePopoverOpen}
              onMouseLeave={handlePopoverClose}
              onClick={handlePopoverOpen}
              sx={{
                padding: '2px',
                color: colors.darkBlue,
                '&:hover': {
                  backgroundColor: colors.darkBlueBlue20,
                  color: colors.darkBlue,
                  opacity: 0.8
                }
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: '16px' }} />
            </IconButton>
          </Flex>
        }
        placeholder="Enter bearer token"
        rows={3}
        characterLimit={500}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        sx={{
          pointerEvents: 'none'
        }}
        disableRestoreFocus
      >
        <Box sx={{ p: 2, maxWidth: 300 }}>
          <Typography variant="body2" sx={{ fontSize: '14px', lineHeight: '20px' }}>
            An Authorization header will be used with format: &apos;Bearer &lt;Your Bearer Token&gt;&apos;
          </Typography>
        </Box>
      </Popover>
    </Box>
  );
}

// Get all possible notification method options
const NOTIFICATION_METHOD_OPTIONS = NotificationMethodSchema.options
  .map((value) => ({
    value,
    label: value,
    disabled: DISABLED_NOTIFICATION_METHODS.includes(value)
  }))
  .sort((a, b) => {
    // Sort enabled options first, then disabled options
    if (a.disabled === b.disabled) return 0;
    return a.disabled ? 1 : -1;
  });

// Convenience wrapper for Notification Methods selection
export function ControlledNotificationMethodsSelect<T extends FieldValues>({ name }: { name: Path<T> }) {
  return (
    <ControlledMultiSelect
      name={name}
      title="Notification Methods"
      description="Select how you want users notified of the alert."
      options={NOTIFICATION_METHOD_OPTIONS}
    />
  );
}

const ENABLED_FREQUENCIES: FrequencyType[] = [FrequencySchema.enum.weekly];

// Create frequency options with disabled states
const getFrequencyOptions = (): DropdownOption[] => {
  return FrequencySchema.options.map((option) => ({
    value: option,
    disabled: !ENABLED_FREQUENCIES.includes(option)
  }));
};

// Webhook configuration section with security type selection
function WebhookConfigSection<T extends FieldValues>() {
  const { control, setValue } = useFormContext<T>();

  // Watch the security type to conditionally render fields
  const securityType = useWatch({
    name: 'notifications.config.webhook.security' as Path<T>,
    control
  });

  return (
    <Box
      sx={{
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        padding: '16px',
        backgroundColor: '#fafafa'
      }}
    >
      <Flex flexDirection="column" gap={16}>
        <Box>
          <Text variant="body2" sx={{ fontWeight: 'bold', marginBottom: '8px', display: 'block' }}>
            Webhook Security Type
          </Text>
          <RadioGroup
            value={securityType || 'urlKey'}
            onChange={(event) => {
              const newSecurityType = event.target.value as 'urlKey' | 'bearerToken';
              setValue('notifications.config.webhook.security' as Path<T>, newSecurityType as PathValue<T, Path<T>>, {
                shouldValidate: true,
                shouldDirty: true
              });

              if (newSecurityType === 'urlKey') {
                // Clear bearer token when switching to URL Key
                setValue('notifications.config.webhook.bearerToken' as Path<T>, undefined as PathValue<T, Path<T>>, {
                  shouldValidate: true,
                  shouldDirty: true
                });
              } else if (newSecurityType === 'bearerToken') {
                // Ensure bearer token field exists when switching to Bearer Token
                setValue('notifications.config.webhook.bearerToken' as Path<T>, '' as PathValue<T, Path<T>>, {
                  shouldValidate: true,
                  shouldDirty: true
                });
              }
            }}
            row
          >
            <FormControlLabel
              value="urlKey"
              control={
                <Radio
                  sx={{
                    '&.Mui-checked': {
                      color: colors.darkBlue
                    },
                    '&.Mui-checked:hover': {
                      backgroundColor: `${colors.darkBlue}14`
                    }
                  }}
                />
              }
              label="URL Key"
            />
            <FormControlLabel
              value="bearerToken"
              control={
                <Radio
                  sx={{
                    '&.Mui-checked': {
                      color: colors.darkBlue
                    },
                    '&.Mui-checked:hover': {
                      backgroundColor: `${colors.darkBlue}14`
                    }
                  }}
                />
              }
              label="Bearer Token"
            />
          </RadioGroup>
        </Box>

        <Flex flexDirection="column" gap={6}>
          <AlertStyledInput<T>
            name={'notifications.config.webhook.url' as Path<T>}
            label="Webhook URL"
            placeholder="Enter webhook URL"
            characterLimit={200}
            defaultValue=""
          />

          {/* Bearer Token field - only shown when bearerToken is selected */}
          {(securityType as string) === 'bearerToken' && <BearerTokenFieldWithInfo<T> />}
        </Flex>
      </Flex>
    </Box>
  );
}

function AlertNotificationsTab<T extends FieldValues>() {
  const { control, setValue } = useFormContext<T>();

  // Watch notification methods and configs directly from form state
  const formNotificationMethodsRaw = useWatch({ control, name: 'notifications.methods' as Path<T> }) as string[];
  const formNotificationMethods = React.useMemo(() => formNotificationMethodsRaw || [], [formNotificationMethodsRaw]);
  const currentEmailConfig = useWatch({ control, name: 'notifications.config.email' as Path<T> });
  const currentWebhookConfig = useWatch({ control, name: 'notifications.config.webhook' as Path<T> });

  // We have to trigger the form validation when methods are unselected/selected otherwise the form will not validate.
  React.useEffect(() => {
    // Handle Email method selection/deselection
    if (formNotificationMethods.includes('Email')) {
      // Ensure email config exists when Email is selected
      if (!currentEmailConfig) {
        // Create proper email config structure when Email is reselected
        setValue('notifications.config.email' as Path<T>, { recipients: [] } as PathValue<T, Path<T>>, {
          shouldValidate: true,
          shouldDirty: true
        });
      }
    } else {
      // Clear email config if Email is unselected
      setValue('notifications.config.email' as Path<T>, undefined as PathValue<T, Path<T>>, {
        shouldValidate: true,
        shouldDirty: true
      });
    }

    // Handle Webhook method selection/deselection
    if (formNotificationMethods.includes('Webhook')) {
      // Ensure webhook config exists when Webhook is selected
      if (!currentWebhookConfig) {
        // Only set default if no config exists (don't override existing data)
        setValue('notifications.config.webhook' as Path<T>, {} as PathValue<T, Path<T>>, {
          shouldValidate: true,
          shouldDirty: true
        });
      }
    } else {
      // Clear webhook config if Webhook is unselected
      setValue('notifications.config.webhook' as Path<T>, undefined as PathValue<T, Path<T>>, {
        shouldValidate: true,
        shouldDirty: true
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formNotificationMethods, setValue]);

  // Use form state directly for conditional rendering
  const showEmailSection = formNotificationMethods.includes('Email');
  const showWebhookSection = formNotificationMethods.includes('Webhook');

  return (
    <Flex flexDirection="column" gap={32}>
      <Flex flexDirection="column" gap={8}>
        <Text variant="h3" sx={{ fontWeight: 600 }}>
          Alert Frequency
        </Text>
        <Text variant="body2" sx={{ fontSize: '16px', fontWeight: 400, lineHeight: '24px' }}>
          Select how often you want to receive alerts.
        </Text>
        <Box sx={{ maxWidth: DROPDOWN_WIDTH }}>
          {/* Alert Frequency - direct Zod path control */}
          <PathControlledDropdown<T>
            name={'queryJSON.basicInfo.frequency' as Path<T>}
            label=""
            options={getFrequencyOptions()}
            placeholder="Select frequency"
            getMappingOption={(v) => {
              switch (v) {
                case FrequencySchema.enum.daily:
                  return 'Daily';
                case FrequencySchema.enum.weekly:
                  return 'Weekly';
                case FrequencySchema.enum.monthly:
                  return 'Monthly';
                case FrequencySchema.enum['when conditions are met']:
                  return 'When conditions are met';
                default:
                  return v;
              }
            }}
          />
        </Box>
      </Flex>

      {/* Notification Methods Selector */}
      <ControlledNotificationMethodsSelect<T> name={'notifications.methods' as Path<T>} />

      {/* Conditionally Rendered Sections */}
      {showEmailSection && (
        <Flex flexDirection="column" gap={16}>
          <Text variant="h3" sx={{ fontWeight: 600, fontSize: '18px' }}>
            Emails
          </Text>
          <EmailAutocomplete<T>
            name={'notifications.config.email.recipients' as Path<T>}
            placeholder="Add up to 10 emails, separated by pressing Enter"
          />
        </Flex>
      )}

      {showWebhookSection && (
        <Flex flexDirection="column" gap={16}>
          <Text variant="h3" sx={{ fontWeight: 600, fontSize: '18px' }}>
            Webhooks
          </Text>
          <WebhookConfigSection<T> />
        </Flex>
      )}
    </Flex>
  );
}

export default AlertNotificationsTab;
